import React, { useEffect, useState } from "react";
import { useNavigate } from "react-router-dom";
import { useAuth } from "@/context/AuthContext";
import {
  crmApi,
  extractCrmError,
  userCanManageCrmSettings,
  userHasCrmAccess,
} from "@/lib/crmApi";
import { Button } from "@/components/ui/button";
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from "@/components/ui/card";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Switch } from "@/components/ui/switch";
import { Tabs, TabsContent, TabsList, TabsTrigger } from "@/components/ui/tabs";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { Textarea } from "@/components/ui/textarea";
import { Badge } from "@/components/ui/badge";
import { toast } from "sonner";
import { Bot, Clock, Contact, Loader2, MessageSquare, MessageSquareText, PhoneCall, ShieldCheck, Trash2 } from "lucide-react";
import GreenApiSettingsPanel from "@/components/crm/GreenApiSettingsPanel";

const AUTO_FIELDS = [
  "enabled",
  "timezone",
  "working_days",
  "open_time",
  "close_time",
  "send_welcome_once_per_day",
  "suppress_if_manager_replied",
  "typing_delay_min_seconds",
  "typing_delay_max_seconds",
  "address_cooldown_hours",
  "enable_saturday",
  "saturday_open_time",
  "saturday_close_time",
];

const TEXT_FIELDS = {
  working_hours_text: "Приветствие в рабочее время",
  after_hours_text: "Приветствие вне рабочего времени",
  address_message_text: "Полный адрес",
  address_short_text: "Короткий ответ с адресом",
  address_keywords_text: "Ключевые слова для отправки адреса",
  stop_phones_text: "Стоп-лист телефонов",
  save_contact_invite_text: "Текст приглашения сохранить контакт",
  call_anchor_fallback_text: "Текст напоминания после звонка",
};

const AUTO_FIELD_LABELS = {
  enabled: "Автоответы включены",
  timezone: "Часовой пояс",
  working_days: "Рабочие дни",
  open_time: "Начало рабочего дня",
  close_time: "Конец рабочего дня",
  send_welcome_once_per_day: "Отправлять приветствие не чаще одного раза в день",
  suppress_if_manager_replied: "Не отвечать, если менеджер уже написал",
  typing_delay_min_seconds: "Минимальная задержка ответа, сек",
  typing_delay_max_seconds: "Максимальная задержка ответа, сек",
  address_cooldown_hours: "Пауза перед повторной отправкой адреса, часов",
  enable_saturday: "Работать по субботам",
  saturday_open_time: "Суббота: начало",
  saturday_close_time: "Суббота: конец",
};

const SAVE_CONTACT_LABELS = {
  save_contact_invite_enabled: "Приглашение сохранить контакт включено",
  save_contact_invite_delay_minutes: "Задержка отправки, минут",
  save_contact_invite_url: "Ссылка на контакт",
};

const CALL_ANCHOR_LABELS = {
  call_anchor_enabled: "Напоминание после звонка включено",
  call_anchor_delay_minutes: "Задержка после звонка, минут",
  call_anchor_suppress_if_manager_replied: "Не отправлять, если менеджер ответил",
  call_anchor_include_address: "Добавлять адрес в сообщение",
};

const SURVEY_LABELS = {
  enabled: "Анкета включена",
  delay_minutes: "Задержка отправки, минут",
  debug_enabled: "Тестовый режим включен",
  debug_phone: "Тестовый телефон",
};

const AI_LABELS = {
  ai_enabled: "ИИ-парсер включен",
  ai_provider: "Провайдер",
  ai_model: "Модель",
  ai_base_url: "Адрес API",
  ai_confidence_auto_apply: "Порог автоприменения",
};

const TELEGRAM_TOGGLE_LABELS = {
  enabled: "CRM-уведомления включены",
  notify_new_client: "Уведомлять о новом клиенте",
  notify_errors: "Уведомлять об ошибках",
  notify_profile_review: "Уведомлять о проверке анкеты",
};

const TELEGRAM_CHAT_TARGET_LABELS = {
  default: "Основной чат",
  clients: "Чат клиентов и заказов",
};

const TELEGRAM_EVENT_GROUP_ORDER = ["crm", "interkom"];

const TELEGRAM_EVENT_GROUP_LABELS = {
  crm: "CRM",
  interkom: "Интерком",
};

const TEMPLATE_TYPE_LABELS = {
  working_hours: "Рабочее время",
  after_hours: "Вне рабочего времени",
  address: "Адрес",
  save_contact_invite: "Сохранить контакт",
  call_anchor: "После звонка",
};

function Field({ label, value, onChange, disabled, type = "text", placeholder }) {
  return (
    <div className="space-y-1.5">
      <Label className="text-sm font-medium text-slate-700">{label}</Label>
      <Input
        type={type}
        value={value ?? ""}
        disabled={disabled}
        placeholder={placeholder}
        onChange={(e) => onChange(e.target.value)}
      />
    </div>
  );
}

function Toggle({ label, checked, onChange, disabled }) {
  return (
    <div className="flex min-h-12 items-center justify-between gap-3 rounded-lg border border-slate-200 bg-white p-3">
      <Label className="text-sm font-medium text-slate-700">{label}</Label>
      <Switch checked={!!checked} disabled={disabled} onCheckedChange={onChange} />
    </div>
  );
}

function SettingsPanel({ title, description, icon: Icon, children }) {
  return (
    <Card className="border-slate-200 bg-white shadow-sm">
      <CardHeader className="border-b border-slate-100 pb-4">
        <CardTitle className="flex items-center gap-2 text-base text-slate-950">
          {Icon && <Icon className="h-4 w-4 text-slate-500" />}
          {title}
        </CardTitle>
        {description && <CardDescription>{description}</CardDescription>}
      </CardHeader>
      <CardContent className="pt-5">{children}</CardContent>
    </Card>
  );
}

export default function CrmSettingsPage() {
  const { user, token, loading: authLoading } = useAuth();
  const navigate = useNavigate();
  const canEdit = userCanManageCrmSettings(user);
  const [auto, setAuto] = useState(null);
  const [templates, setTemplates] = useState([]);
  const [survey, setSurvey] = useState(null);
  const [telegram, setTelegram] = useState(null);
  const [diagnostics, setDiagnostics] = useState(null);
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [newTemplate, setNewTemplate] = useState({
    template_type: "working_hours",
    text: "",
    is_active: true,
    sort_order: 0,
  });

  useEffect(() => {
    if (!authLoading && !userHasCrmAccess(user)) navigate("/");
  }, [authLoading, user, navigate]);

  const load = async () => {
    if (!token) return;
    setLoading(true);
    try {
      const [a, t, s, tg, d] = await Promise.all([
        crmApi.getAutoReplySettings(token),
        crmApi.listWelcomeTemplates(token),
        crmApi.getProfileSurveySettings(token),
        crmApi.getTelegramSettings(token),
        crmApi.getDiagnostics(token),
      ]);
      setAuto(a);
      setTemplates(t);
      setSurvey(s);
      setTelegram(tg);
      setDiagnostics(d);
    } catch (err) {
      toast.error(extractCrmError(err, "Не удалось загрузить настройки"));
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    load();
  }, [token]);

  const saveAuto = async () => {
    setSaving(true);
    try {
      setAuto(await crmApi.patchAutoReplySettings(token, auto));
      toast.success("Настройки сохранены");
    } catch (err) {
      toast.error(extractCrmError(err));
    } finally {
      setSaving(false);
    }
  };

  const saveSurvey = async () => {
    setSaving(true);
    try {
      setSurvey(await crmApi.patchProfileSurveySettings(token, survey));
      toast.success("Анкета сохранена");
    } catch (err) {
      toast.error(extractCrmError(err));
    } finally {
      setSaving(false);
    }
  };

  const saveTelegram = async () => {
    setSaving(true);
    try {
      setTelegram(await crmApi.patchTelegramSettings(token, telegram));
      toast.success("Настройки Telegram сохранены");
    } catch (err) {
      toast.error(extractCrmError(err));
    } finally {
      setSaving(false);
    }
  };

  const updateTelegramRouting = (eventType, chatTarget) => {
    setTelegram({
      ...telegram,
      routing: { ...(telegram?.routing || {}), [eventType]: chatTarget },
      events: (telegram?.events || []).map((event) =>
        event.event_type === eventType ? { ...event, chat_target: chatTarget } : event
      ),
    });
  };

  const updateTemplate = async (tpl, patch) => {
    if (!canEdit) return;
    try {
      await crmApi.patchWelcomeTemplate(token, tpl.id, { ...tpl, ...patch });
      await load();
    } catch (err) {
      toast.error(extractCrmError(err));
    }
  };

  const createTemplate = async (e) => {
    e.preventDefault();
    if (!canEdit) return;
    setSaving(true);
    try {
      await crmApi.createWelcomeTemplate(token, newTemplate);
      setNewTemplate({
        template_type: "working_hours",
        text: "",
        is_active: true,
        sort_order: 0,
      });
      await load();
      toast.success("Шаблон создан");
    } catch (err) {
      toast.error(extractCrmError(err));
    } finally {
      setSaving(false);
    }
  };

  const deleteTemplate = async (tpl) => {
    if (!window.confirm("Удалить шаблон?")) return;
    try {
      await crmApi.deleteWelcomeTemplate(token, tpl.id);
      await load();
    } catch (err) {
      toast.error(extractCrmError(err));
    }
  };

  if (loading || !auto || !survey || !telegram) {
    return (
      <div className="flex items-center gap-2 p-8 text-sm text-slate-600">
        <Loader2 className="h-4 w-4 animate-spin" />
        Загружаем настройки CRM...
      </div>
    );
  }

  return (
    <div className="mx-auto max-w-6xl space-y-5 p-4 sm:p-6">
      <div className="flex flex-col gap-3 lg:flex-row lg:items-end lg:justify-between">
        <div>
          <Badge variant="outline" className="mb-3 border-slate-200 bg-white text-slate-600">
            Администрирование CRM
          </Badge>
          <h1 className="text-3xl font-semibold tracking-tight text-slate-950">Настройки CRM</h1>
          <p className="mt-2 max-w-2xl text-sm text-slate-500">
            Управление автоответами, шаблонами сообщений, анкетой клиента, Green API и диагностикой интеграций.
          </p>
        </div>
        {!canEdit && (
          <div className="rounded-lg border border-amber-200 bg-amber-50 px-4 py-3 text-sm text-amber-800">
            У вас есть доступ на просмотр. Изменять настройки может только администратор.
          </div>
        )}
      </div>

      <Tabs defaultValue="auto" className="space-y-4">
        <TabsList className="grid h-auto w-full grid-cols-2 gap-1 bg-slate-100 p-1 md:grid-cols-4 lg:grid-cols-9">
          <TabsTrigger value="auto">Автоответы</TabsTrigger>
          <TabsTrigger value="templates">Шаблоны</TabsTrigger>
          <TabsTrigger value="save">Контакт</TabsTrigger>
          <TabsTrigger value="call">После звонка</TabsTrigger>
          <TabsTrigger value="survey">Анкета клиента</TabsTrigger>
          <TabsTrigger value="ai">ИИ</TabsTrigger>
          <TabsTrigger value="telegram">Telegram</TabsTrigger>
          <TabsTrigger value="greenapi">Green API</TabsTrigger>
          <TabsTrigger value="diag">Диагностика</TabsTrigger>
        </TabsList>

        <TabsContent value="auto">
          <SettingsPanel
            title="Автоответы WhatsApp"
            description="График работы, задержки отправки и основные тексты, которые клиент видит в переписке."
            icon={MessageSquareText}
          >
            <div className="grid grid-cols-1 gap-3 md:grid-cols-3">
              {AUTO_FIELDS.map((key) =>
                typeof auto[key] === "boolean" ? (
                  <Toggle key={key} label={AUTO_FIELD_LABELS[key] || key} checked={auto[key]} disabled={!canEdit} onChange={(v) => setAuto({ ...auto, [key]: v })} />
                ) : (
                  <Field key={key} label={AUTO_FIELD_LABELS[key] || key} value={auto[key]} disabled={!canEdit} onChange={(v) => setAuto({ ...auto, [key]: v })} />
                )
              )}
              <div className="md:col-span-3 grid grid-cols-1 gap-3 md:grid-cols-2">
                {Object.entries(TEXT_FIELDS).slice(0, 6).map(([key, label]) => (
                  <div key={key} className="space-y-1.5">
                    <Label className="text-sm font-medium text-slate-700">{label}</Label>
                    <Textarea rows={5} value={auto[key] || ""} disabled={!canEdit} onChange={(e) => setAuto({ ...auto, [key]: e.target.value })} />
                  </div>
                ))}
              </div>
            </div>
            {canEdit && (
              <div className="mt-5">
                <Button disabled={saving} onClick={saveAuto}>
                  {saving && <Loader2 className="h-4 w-4 animate-spin" />}
                  Сохранить автоответы
                </Button>
              </div>
            )}
          </SettingsPanel>
        </TabsContent>

        <TabsContent value="templates">
          <SettingsPanel
            title="Шаблоны сообщений"
            description="Готовые тексты для приветствий и сервисных сообщений. Изменения применяются после сохранения поля."
            icon={MessageSquareText}
          >
            <div className="space-y-4">
              {canEdit && (
                <form onSubmit={createTemplate} className="rounded-lg border border-slate-200 bg-slate-50 p-4 space-y-3">
                  <div className="grid grid-cols-1 gap-3 md:grid-cols-[220px_140px_1fr_auto]">
                    <div className="space-y-1.5">
                      <Label className="text-sm font-medium text-slate-700">Тип шаблона</Label>
                      <Select
                        value={newTemplate.template_type}
                        onValueChange={(v) => setNewTemplate({ ...newTemplate, template_type: v })}
                      >
                        <SelectTrigger><SelectValue /></SelectTrigger>
                        <SelectContent>
                          {Object.entries(TEMPLATE_TYPE_LABELS).map(([value, label]) => (
                            <SelectItem key={value} value={value}>{label}</SelectItem>
                          ))}
                        </SelectContent>
                      </Select>
                    </div>
                    <Field
                      type="number"
                      label="Порядок"
                      value={newTemplate.sort_order}
                      onChange={(v) => setNewTemplate({ ...newTemplate, sort_order: v })}
                    />
                    <Toggle
                      label="Шаблон активен"
                      checked={newTemplate.is_active}
                      onChange={(v) => setNewTemplate({ ...newTemplate, is_active: v })}
                    />
                    <div className="flex items-end">
                      <Button type="submit" disabled={saving || !newTemplate.text.trim()}>
                        Добавить
                      </Button>
                    </div>
                  </div>
                  <div className="space-y-1.5">
                    <Label className="text-sm font-medium text-slate-700">Текст шаблона</Label>
                    <Textarea
                      rows={3}
                      value={newTemplate.text}
                      onChange={(e) => setNewTemplate({ ...newTemplate, text: e.target.value })}
                      placeholder="Введите текст сообщения"
                    />
                  </div>
                </form>
              )}
              {templates.map((tpl) => (
                <div key={tpl.id} className="rounded-lg border border-slate-200 p-4 space-y-3">
                  <div className="grid grid-cols-1 gap-3 md:grid-cols-[220px_1fr_auto] md:items-end">
                    <div className="space-y-1.5">
                      <Label className="text-sm font-medium text-slate-700">Тип шаблона</Label>
                      <Select
                        value={tpl.template_type}
                        disabled={!canEdit}
                        onValueChange={(v) => updateTemplate(tpl, { template_type: v })}
                      >
                        <SelectTrigger><SelectValue /></SelectTrigger>
                        <SelectContent>
                          {Object.entries(TEMPLATE_TYPE_LABELS).map(([value, label]) => (
                            <SelectItem key={value} value={value}>{label}</SelectItem>
                          ))}
                        </SelectContent>
                      </Select>
                    </div>
                    <Toggle label="Шаблон активен" checked={tpl.is_active} disabled={!canEdit} onChange={(v) => updateTemplate(tpl, { is_active: v })} />
                    {canEdit && (
                      <Button variant="outline" size="sm" onClick={() => deleteTemplate(tpl)}>
                        <Trash2 className="h-4 w-4" />
                        Удалить
                      </Button>
                    )}
                  </div>
                  <div className="space-y-1.5">
                    <Label className="text-sm font-medium text-slate-700">Текст сообщения</Label>
                    <Textarea rows={4} defaultValue={tpl.text} disabled={!canEdit} onBlur={(e) => updateTemplate(tpl, { text: e.target.value })} />
                  </div>
                </div>
              ))}
            </div>
          </SettingsPanel>
        </TabsContent>

        <TabsContent value="save">
          <SettingsPanel
            title="Приглашение сохранить контакт"
            description="Сообщение, которое помогает клиенту сохранить номер магазина после первого обращения."
            icon={Contact}
          >
            <div className="grid grid-cols-1 gap-3 md:grid-cols-2">
              <Toggle label={SAVE_CONTACT_LABELS.save_contact_invite_enabled} checked={auto.save_contact_invite_enabled} disabled={!canEdit} onChange={(v) => setAuto({ ...auto, save_contact_invite_enabled: v })} />
              <Field label={SAVE_CONTACT_LABELS.save_contact_invite_delay_minutes} value={auto.save_contact_invite_delay_minutes} disabled={!canEdit} onChange={(v) => setAuto({ ...auto, save_contact_invite_delay_minutes: v })} />
              <Field label={SAVE_CONTACT_LABELS.save_contact_invite_url} value={auto.save_contact_invite_url} disabled={!canEdit} onChange={(v) => setAuto({ ...auto, save_contact_invite_url: v })} />
              <div className="space-y-1.5 md:col-span-2">
                <Label className="text-sm font-medium text-slate-700">{TEXT_FIELDS.save_contact_invite_text}</Label>
                <Textarea rows={5} value={auto.save_contact_invite_text || ""} disabled={!canEdit} onChange={(e) => setAuto({ ...auto, save_contact_invite_text: e.target.value })} />
              </div>
            </div>
            {canEdit && <Button className="mt-5" disabled={saving} onClick={saveAuto}>Сохранить приглашение</Button>}
          </SettingsPanel>
        </TabsContent>

        <TabsContent value="call">
          <SettingsPanel
            title="Напоминание после звонка"
            description="Автоматическое сообщение после телефонного контакта с клиентом."
            icon={PhoneCall}
          >
            <div className="grid grid-cols-1 gap-3 md:grid-cols-2">
              <Toggle label={CALL_ANCHOR_LABELS.call_anchor_enabled} checked={auto.call_anchor_enabled} disabled={!canEdit} onChange={(v) => setAuto({ ...auto, call_anchor_enabled: v })} />
              <Field label={CALL_ANCHOR_LABELS.call_anchor_delay_minutes} value={auto.call_anchor_delay_minutes} disabled={!canEdit} onChange={(v) => setAuto({ ...auto, call_anchor_delay_minutes: v })} />
              <Toggle label={CALL_ANCHOR_LABELS.call_anchor_suppress_if_manager_replied} checked={auto.call_anchor_suppress_if_manager_replied} disabled={!canEdit} onChange={(v) => setAuto({ ...auto, call_anchor_suppress_if_manager_replied: v })} />
              <Toggle label={CALL_ANCHOR_LABELS.call_anchor_include_address} checked={auto.call_anchor_include_address} disabled={!canEdit} onChange={(v) => setAuto({ ...auto, call_anchor_include_address: v })} />
              <div className="space-y-1.5 md:col-span-2">
                <Label className="text-sm font-medium text-slate-700">{TEXT_FIELDS.call_anchor_fallback_text}</Label>
                <Textarea rows={5} value={auto.call_anchor_fallback_text || ""} disabled={!canEdit} onChange={(e) => setAuto({ ...auto, call_anchor_fallback_text: e.target.value })} />
              </div>
            </div>
            {canEdit && <Button className="mt-5" disabled={saving} onClick={saveAuto}>Сохранить напоминание</Button>}
          </SettingsPanel>
        </TabsContent>

        <TabsContent value="survey">
          <SettingsPanel
            title="Анкета клиента"
            description="Сообщение для уточнения автомобиля и контактных данных."
            icon={Clock}
          >
            <div className="grid grid-cols-1 gap-3 md:grid-cols-2">
              <Toggle label={SURVEY_LABELS.enabled} checked={survey.enabled} disabled={!canEdit} onChange={(v) => setSurvey({ ...survey, enabled: v })} />
              <Field label={SURVEY_LABELS.delay_minutes} value={survey.delay_minutes} disabled={!canEdit} onChange={(v) => setSurvey({ ...survey, delay_minutes: v })} />
              <Toggle label={SURVEY_LABELS.debug_enabled} checked={survey.debug_enabled} disabled={!canEdit} onChange={(v) => setSurvey({ ...survey, debug_enabled: v })} />
              <Field label={SURVEY_LABELS.debug_phone} value={survey.debug_phone} disabled={!canEdit} onChange={(v) => setSurvey({ ...survey, debug_phone: v })} />
              <div className="space-y-1.5 md:col-span-2">
                <Label className="text-sm font-medium text-slate-700">Текст анкеты</Label>
                <Textarea rows={6} value={survey.survey_text || ""} disabled={!canEdit} onChange={(e) => setSurvey({ ...survey, survey_text: e.target.value })} />
              </div>
            </div>
            {canEdit && <Button className="mt-5" disabled={saving} onClick={saveSurvey}>Сохранить анкету</Button>}
          </SettingsPanel>
        </TabsContent>

        <TabsContent value="ai">
          <SettingsPanel
            title="ИИ-парсер запросов"
            description="Автоматическое распознавание автомобиля, запчасти и уверенности из сообщений клиента."
            icon={Bot}
          >
            <div className="grid grid-cols-1 gap-3 md:grid-cols-2">
              <Toggle label={AI_LABELS.ai_enabled} checked={survey.ai_enabled} disabled={!canEdit} onChange={(v) => setSurvey({ ...survey, ai_enabled: v })} />
              <Field label={AI_LABELS.ai_provider} value={survey.ai_provider} disabled={!canEdit} onChange={(v) => setSurvey({ ...survey, ai_provider: v })} />
              <Field label={AI_LABELS.ai_model} value={survey.ai_model} disabled={!canEdit} onChange={(v) => setSurvey({ ...survey, ai_model: v })} />
              <Field label={AI_LABELS.ai_base_url} value={survey.ai_base_url} disabled={!canEdit} onChange={(v) => setSurvey({ ...survey, ai_base_url: v })} />
              <Field label={AI_LABELS.ai_confidence_auto_apply} value={survey.ai_confidence_auto_apply} disabled={!canEdit} onChange={(v) => setSurvey({ ...survey, ai_confidence_auto_apply: v })} />
              <div className="rounded-lg border border-slate-200 bg-slate-50 p-3 text-sm">
                <p className="font-medium text-slate-700">Ключ API</p>
                <Badge className={survey.ai_api_key_configured ? "mt-2 bg-emerald-100 text-emerald-700" : "mt-2 bg-amber-100 text-amber-800"}>
                  {survey.ai_api_key_configured ? "Настроен" : "Не настроен"}
                </Badge>
                <p className="mt-2 text-xs text-slate-500">Ключ хранится на сервере и не отображается в интерфейсе.</p>
              </div>
            </div>
            {canEdit && <Button className="mt-5" disabled={saving} onClick={saveSurvey}>Сохранить ИИ-настройки</Button>}
          </SettingsPanel>
        </TabsContent>

        <TabsContent value="telegram">
          <SettingsPanel
            title="Telegram-уведомления"
            description="Настройка чатов и маршрутизации уведомлений CRM и Интерком. Основной чат используется для системных событий, отдельный чат — для клиентов и заказов."
            icon={ShieldCheck}
          >
            <div className="space-y-6">
              <div className="grid grid-cols-1 gap-3 md:grid-cols-2">
                {Object.entries(TELEGRAM_TOGGLE_LABELS).map(([key, label]) => (
                  <Toggle
                    key={key}
                    label={label}
                    checked={telegram[key]}
                    disabled={!canEdit}
                    onChange={(v) => setTelegram({ ...telegram, [key]: v })}
                  />
                ))}
                <div className="rounded-lg border border-slate-200 bg-slate-50 p-3 text-sm md:col-span-2">
                  <p className="font-medium text-slate-700">Telegram-бот</p>
                  <Badge className={telegram.bot_configured ? "mt-2 bg-emerald-100 text-emerald-700" : "mt-2 bg-amber-100 text-amber-800"}>
                    {telegram.bot_configured ? "Токен настроен" : "Токен не настроен"}
                  </Badge>
                  <p className="mt-2 text-xs text-slate-500">Токен бота задаётся в .env и не редактируется здесь.</p>
                </div>
              </div>

              <div className="grid grid-cols-1 gap-3 md:grid-cols-2">
                <Field
                  label="Основной chat_id"
                  value={telegram.chat_id}
                  disabled={!canEdit}
                  placeholder="-1001234567890"
                  onChange={(v) => setTelegram({ ...telegram, chat_id: v })}
                />
                <Field
                  label="Chat_id клиентов и заказов"
                  value={telegram.clients_chat_id}
                  disabled={!canEdit}
                  placeholder="-1009876543210"
                  onChange={(v) => setTelegram({ ...telegram, clients_chat_id: v })}
                />
              </div>

              <div className="space-y-6">
                <div>
                  <h3 className="text-sm font-semibold text-slate-900">Маршрутизация уведомлений</h3>
                  <p className="mt-1 text-sm text-slate-500">
                    Выберите, в какой чат отправлять каждый тип уведомления.
                  </p>
                </div>
                {TELEGRAM_EVENT_GROUP_ORDER.map((groupKey) => {
                  const groupEvents = (telegram.events || []).filter(
                    (event) => (event.group || "crm") === groupKey
                  );
                  if (!groupEvents.length) return null;
                  return (
                    <div key={groupKey} className="space-y-3">
                      <h4 className="text-xs font-semibold uppercase tracking-wide text-slate-500">
                        {TELEGRAM_EVENT_GROUP_LABELS[groupKey] || groupKey}
                      </h4>
                      <div className="overflow-hidden rounded-lg border border-slate-200">
                        <div className="grid grid-cols-[1fr_220px] gap-3 border-b border-slate-200 bg-slate-50 px-4 py-2 text-xs font-semibold uppercase tracking-wide text-slate-500">
                          <span>Тип уведомления</span>
                          <span>Чат</span>
                        </div>
                        {groupEvents.map((event) => (
                          <div
                            key={event.event_type}
                            className="grid grid-cols-1 gap-3 border-b border-slate-100 px-4 py-3 last:border-b-0 md:grid-cols-[1fr_220px] md:items-center"
                          >
                            <div>
                              <p className="text-sm font-medium text-slate-900">{event.label}</p>
                              <p className="text-xs text-slate-500">{event.event_type}</p>
                            </div>
                            <Select
                              value={event.chat_target}
                              disabled={!canEdit}
                              onValueChange={(v) => updateTelegramRouting(event.event_type, v)}
                            >
                              <SelectTrigger>
                                <SelectValue />
                              </SelectTrigger>
                              <SelectContent>
                                {Object.entries(TELEGRAM_CHAT_TARGET_LABELS).map(([value, label]) => (
                                  <SelectItem key={value} value={value}>
                                    {label}
                                  </SelectItem>
                                ))}
                              </SelectContent>
                            </Select>
                          </div>
                        ))}
                      </div>
                    </div>
                  );
                })}
              </div>

              {canEdit && (
                <Button disabled={saving} onClick={saveTelegram}>
                  {saving && <Loader2 className="h-4 w-4 animate-spin" />}
                  Сохранить Telegram
                </Button>
              )}
            </div>
          </SettingsPanel>
        </TabsContent>

        <TabsContent value="greenapi">
          <GreenApiSettingsPanel token={token} canEdit={canEdit} />
        </TabsContent>

        <TabsContent value="diag">
          <SettingsPanel
            title="Диагностика"
            description="Служебная информация от сервера для проверки подключений и очередей."
            icon={ShieldCheck}
          >
            <pre className="max-h-[520px] overflow-auto rounded-lg bg-slate-950 p-4 text-xs text-slate-100 whitespace-pre-wrap">
              {JSON.stringify(diagnostics, null, 2)}
            </pre>
          </SettingsPanel>
        </TabsContent>
      </Tabs>
    </div>
  );
}
